/**
 * YoolPayDia — el día, que es donde el ciclo se ejecuta.
 *
 * QUÉ REEMPLAZA
 * -------------
 * Esta pantalla tenía 1.989 líneas y SIETE secciones de análisis debajo de los pasos:
 * cuadre, ciclo de 7 etapas por cobro, tesorería, producción/cobranza, recaudado,
 * contabilidad y próximos días. Cada una respondía una pregunta legítima, pero ninguna era
 * el trabajo del día, y todas juntas lo enterraban: había que bajar por seis bloques para
 * saber si faltaba aceptar un depósito.
 *
 * Ahora son DOS bloques:
 *
 *   1. LOS SEIS PASOS   qué falta y qué botón lo hace. Es lo único que instruye.
 *   2. LA PLATA         a quién se le debe girar, con el detalle por transacción —
 *                       incluida la recaudación compartida— y el traspaso que hay que
 *                       hacer desde Collection antes de girar.
 *
 * Lo que se fue no se reemplazó por nada: era diagnóstico, no operación. El ciclo por cobro
 * y el ledger fila por fila se consultaban una vez al mes y ocupaban la pantalla todos los
 * días.
 *
 * LOS DOS COMPONENTES SON DEL YOOL DS, y eso también es la simplificación. La versión vieja
 * tenía su propia tesorería escrita con Card/Table de shadcn mientras
 * `components/payments/yoolpay/TesoreriaDelDia.jsx` —la misma vista, en el DS y con 281
 * líneas de test— quedaba sin usar. Dos implementaciones de la misma plata es exactamente el
 * problema que este trabajo viene a cerrar.
 *
 * DESPUÉS SE SUMARON DOS SECCIONES MÁS, la conciliación contra el PSP y el buscador de
 * soporte: `ConciliacionDelDia` (cruce línea a línea del día contra `/api/conciliacion`) y
 * `BuscadorCobro` (buscar un cobro puntual por su id del PSP, sin fecha). Ninguna de las dos
 * depende de que `/api/dia` haya respondido bien —tienen su propia carga— así que se
 * renderizan siempre, no sólo cuando el resto del día cargó.
 *
 * NADA SE CALCULA AQUÍ. El estado de los pasos lo decide `cierre-dia.service` en payment y
 * las cifras salen de `lib/yoolpay/tesoreriaVista.js`. Si esta pantalla y el mes mostraran
 * cifras distintas de la misma plata, la culpa sería de haber calculado acá.
 *
 * @see src/components/payments/yoolpay/CierreDelDia.jsx — los seis pasos
 * @see src/components/payments/yoolpay/TesoreriaDelDia.jsx — la plata, en el DS
 * @see src/components/payments/yoolpay/ConciliacionDelDia.jsx — la conciliación del día
 * @see src/components/payments/yoolpay/BuscadorCobro.jsx — el buscador de soporte
 * @see src/pages/payments/yoolpay/YoolPayMes.jsx — el mes, de donde se entra
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

import {
  aceptarDeposito,
  crearPedidos,
  exportarAsientos,
  getDia,
  registrarGiroDelDia,
} from '../../../services/yoolpay/diaApi';
import CierreDelDia from '../../../components/payments/yoolpay/CierreDelDia';
import TesoreriaDelDia from '../../../components/payments/yoolpay/TesoreriaDelDia';
import ConciliacionDelDia from '../../../components/payments/yoolpay/ConciliacionDelDia';
import CuadraturaDelDia from '../../../components/payments/yoolpay/CuadraturaDelDia';
import ModalGiros from '../../../components/payments/yoolpay/ModalGiros';
import PagosDivididos from '../../../components/payments/yoolpay/PagosDivididos';
import TransaccionesDelDia from '../../../components/payments/yoolpay/TransaccionesDelDia';
import RespaldoOdoo from '../../../components/payments/yoolpay/RespaldoOdoo';
import BuscadorCobro from '../../../components/payments/yoolpay/BuscadorCobro';
import { PedidosDelDia } from '../../../components/payments/yoolpay/AsientosYGiros';
import { Aviso, Seccion } from '../../../components/payments/yoolpay/cierre/piezas';
import { Boton } from '../../../components/payments/yoolpay/cierre/ds';
import {
  FECHA_RE,
  fmtDiaSemana,
  fmtFechaLarga,
  hoyUtc,
  periodoDe,
  sumarDias,
} from '../../../lib/yoolpay/formato';

export default function YoolPayDia() {
  // Sólo el token. El autor de las dos acciones del cierre —aceptar un depósito, registrar
  // un giro— lo resuelve payment desde este mismo JWT: antes se mandaba el `userId` en el
  // body y quedaba escrito tal cual, o sea que el cliente firmaba por quien quisiera.
  const { getToken } = useAuth();

  // EL DÍA VIENE EN LA URL para que el mes pueda linkear a uno concreto, y para que el link
  // se pueda pegar en un chat. Sin `?fecha` se abre hoy.
  const [searchParams, setSearchParams] = useSearchParams();
  const fecha = searchParams.get('fecha') ?? hoyUtc();
  const setFecha = useCallback(
    (f) => setSearchParams(typeof f === 'function' ? { fecha: f(fecha) } : { fecha: f },
      { replace: true }),
    [fecha, setSearchParams],
  );

  const fechaValida = FECHA_RE.test(fecha) ? fecha : null;
  const hoy = hoyUtc();
  const navigate = useNavigate();

  // `data === null` es «todavía no hay respuesta buena» (cargando o error); un objeto es una
  // respuesta real. Distinguirlos es la razón de ser de esta pantalla: los ceros de una
  // respuesta fallida se leen como «día tranquilo».
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  /** El error de la última acción ejecutada. Separado del de carga: son cosas distintas. */
  const [accionError, setAccionError] = useState(null);

  // El paso 6 en un solo paso: el modal con todas las compañías que faltan. `resultados`
  // es por compañía —`{ [compania_id]: { ok, codigo } | { ok:false, error } }`— para que un
  // fallo se vea en SU fila y no obligue a reintentar las que ya salieron.
  const [girosAbierto, setGirosAbierto] = useState(false);
  const [girosCargando, setGirosCargando] = useState(false);
  const [girosResultados, setGirosResultados] = useState(null);
  /**
   * Lo que la última acción devolvió cuando SÍ funcionó.
   *
   * Sin esto, exportar con la cola vacía no dejaba rastro: la pantalla recargaba, los seis
   * pasos quedaban igual, y no había cómo distinguir «no había nada que hacer» de «el botón
   * no hizo nada». También es donde aparece la advertencia de los eventos que fallan y no
   * son de este día.
   */
  const [accionResultado, setAccionResultado] = useState(null);

  const load = useCallback(async () => {
    if (!fechaValida) return;
    setCargando(true);
    setError(null);
    try {
      const token = await getToken();
      setData(await getDia(token, { fecha: fechaValida }));
    } catch (e) {
      setData(null);
      setError(e?.message ?? 'No se pudo cargar el día');
    } finally {
      setCargando(false);
    }
  }, [getToken, fechaValida]);

  useEffect(() => { load(); }, [load]);

  const ejecutarAccion = useCallback(async (fn) => {
    setAccionError(null);
    setAccionResultado(null);
    try {
      const token = await getToken();
      const r = await fn(token);
      // El resultado se guarda ANTES de recargar: `load()` no lo toca, y así el mensaje
      // sigue en pantalla junto a los pasos ya actualizados.
      if (r?.mensaje || r?.advertencia) {
        setAccionResultado({ mensaje: r.mensaje ?? null, advertencia: r.advertencia ?? null });
      }
      await load();
    } catch (e) {
      setAccionError(e?.message ?? 'No se pudo ejecutar la acción');
    }
  }, [getToken, load]);

  const accionesDelCierre = useMemo(() => {
    if (!fechaValida) return {};
    const psp = data?.cierre?.psp_id ?? 'virtualpos';

    return {
      aceptar_deposito: () => {
        // NO SE PIDE NADA. El sistema ya leyó la cartola de Odoo y `sugerirDeposito`
        // identificó la línea: el monto y la fecha vienen del banco. Preguntarlos sería
        // pedir que alguien copie a mano un dato que ya tenemos, y arriesgar que escriba
        // una cifra distinta de la que dice el extracto.
        const sug = (data?.cierre?.pasos ?? [])
          .find((p) => p.clave === 'aceptar_deposito')?.sugerido;
        if (!sug) {
          setAccionError(
            'No hay una línea de cartola identificada para este día. Recarga la pantalla; '
            + 'si el paso sigue bloqueado, el motivo está en su fila.',
          );
          return;
        }
        ejecutarAccion((token) => aceptarDeposito(token, {
          fecha: fechaValida,
          psp,
          // Lo que dice el BANCO. Si difiere del esperado, esa diferencia es el descuadre
          // que hay que resolver con el proveedor — no algo que se ajusta callado.
          monto_clp: sug.montoClp,
          fecha_extracto: sug.fecha,
          cartola: sug.cartola ?? undefined,
          // EL ID DE LA LÍNEA, que es lo que payment verifica antes de escribir.
          // Sin él responde 422: la afirmación «la plata llegó» habilita el giro,
          // así que tiene que quedar apuntando a la línea del banco que la
          // respalda. Ya lo teníamos acá y se estaba descartando.
          odoo_statement_line_id: sug.id,
        }));
      },

      // PASO 4 — postea el asiento del día en Odoo (`DE-YYMM-nnnn`).
      //
      // LA FECHA VA SIEMPRE Y ACOTA LA ACCIÓN AL DÍA. Este comentario decía lo contrario
      // —«no se pide nada y no se acota al día», porque antes drenaba la cola global— y
      // quedó falso el 2026-08-25, cuando el paso pasó a postear el lote del día. Sin la
      // fecha payment responde 400.
      contabilidad: () => ejecutarAccion(
        (token) => exportarAsientos(token, { fecha: fechaValida, psp }),
      ),

      // PASO 5 — crea los pedidos de venta y compra del día en Odoo.
      pedidos: () => ejecutarAccion((token) => crearPedidos(token, { fecha: fechaValida, psp })),

      // PASO 6 — abre el modal con TODAS las compañías que faltan.
      //
      // Antes esto encadenaba `window.prompt`: uno por la referencia, otro por la
      // justificación, y había que volver a apretar el botón por cada compañía. Con tres
      // eran seis diálogos del navegador, sin ver los montos y sin poder corregir el
      // anterior. Acá sólo se abre; lo que se manda lo arma `ModalGiros`.
      giro: () => setGirosAbierto(true),
    };
  }, [data, fechaValida, ejecutarAccion]);

  /**
   * Las compañías que todavía esperan su giro.
   *
   * SE EXCLUYEN LAS YA GIRADAS: `a_girar_clp` es lo que se recaudó de esa compañía, no lo
   * que queda por girarle, así que sigue en positivo después de girada.
   */
  const girosPendientes = useMemo(() => {
    const giradas = new Set(data?.cierre?.giradas ?? []);
    return (data?.tesoreria?.por_compania ?? [])
      .filter((c) => c.total?.a_girar_clp > 0 && !giradas.has(c.compania_id))
      .map((c) => ({
        compania_id: c.compania_id,
        compania_nombre: c.compania_nombre,
        // El monto NO se edita: payment lo verifica contra lo recaudado y responde 422 si
        // no calza. Es la regla del paso 6 — el 100% del bruto de sus cobros del día.
        monto_clp: c.total.a_girar_clp,
      }));
  }, [data]);

  /**
   * Registra los giros elegidos, uno por llamada.
   *
   * DE A UNO Y NO EN LOTE, aunque el modal los pida juntos: cada giro es una transferencia
   * distinta con su propia referencia, y el endpoint ya es idempotente por
   * (fecha, psp, compañía). Mandarlos en un solo request obligaría a inventar un formato
   * de resultado parcial en el servidor para decir cuál de las tres falló.
   *
   * SECUENCIAL Y NO EN PARALELO: cada uno postea un asiento en Odoo, y tres a la vez sobre
   * el mismo diario es pedir un choque de secuencias por nada — son tres llamadas.
   *
   * NO SE CORTA AL PRIMER ERROR. Si una compañía falla, las otras se registran igual y su
   * error queda en SU fila: cortar dejaría el día a medias sin decir dónde quedó.
   */
  const registrarGiros = useCallback(async (giros, justificacion) => {
    setGirosCargando(true);
    setAccionError(null);
    const psp = data?.cierre?.psp_id ?? 'virtualpos';
    const salida = {};
    try {
      const token = await getToken();
      for (const g of giros) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const r = await registrarGiroDelDia(token, {
            fecha: fechaValida,
            psp,
            compania_id: g.compania_id,
            // Para la glosa del asiento que cierra el pasivo.
            compania_nombre: g.compania_nombre ?? undefined,
            monto_clp: g.monto_clp,
            referencia: g.referencia,
            via: 'manual',
            fecha_transferencia: fechaValida,
            justificacion,
          });
          salida[g.compania_id] = { ok: true, codigo: r?.giro?.codigo ?? null };
        } catch (e) {
          salida[g.compania_id] = { ok: false, error: e?.message ?? 'No se pudo registrar.' };
        }
        setGirosResultados({ ...salida });
      }
    } catch (e) {
      setAccionError(e?.message ?? 'No se pudo obtener la sesión.');
    } finally {
      setGirosCargando(false);
      // Se recarga siempre: aunque alguna haya fallado, las que sí salieron cambian el día.
      load();
    }
  }, [data, fechaValida, getToken, load]);

  const periodo = periodoDe(fechaValida) ?? hoy.slice(0, 7);

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18, width: '100%', minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
      <Seccion
        titulo={fechaValida ? fmtFechaLarga(fechaValida) : 'Elige un día'}
        bajada={
          fechaValida
            ? `${fmtDiaSemana(fechaValida)}${fechaValida === hoy ? ' · hoy' : ''}`
              + '. El PSP deposita el lote de este día, y de aquí se gira a cada compañía.'
            : 'La fecha tiene que ser YYYY-MM-DD.'
        }
        derecha={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {/* VOLVER AL MES es la única navegación que queda, y va primero: el mes es de
                donde se entra, y sin esto el día es un callejón. */}
            <Link
              to={`/yoolpay/mes?period=${periodo}`}
              style={{ fontSize: 13, color: 'var(--yool-cta)', fontWeight: 600 }}
            >
              ← Volver al mes
            </Link>
            <Boton
              variant="outline"
              onClick={() => setFecha((f) => sumarDias(f, -1))}
              disabled={!fechaValida}
              aria-label="Día anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Boton>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              style={{
                fontSize: 13,
                padding: '7px 10px',
                border: '1px solid var(--yool-border)',
                borderRadius: 8,
              }}
            />
            <Boton
              variant="outline"
              onClick={() => setFecha((f) => sumarDias(f, 1))}
              disabled={!fechaValida}
              aria-label="Día siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </Boton>
            <Boton variant="outline" onClick={load} disabled={cargando || !fechaValida}>
              <RefreshCw className={`h-4 w-4 ${cargando ? 'animate-spin' : ''}`} />
            </Boton>
            {/* El documento que se le manda a la compañía. Va al final y en `outline`: no
                es un paso del cierre, es lo que sale una vez que el día ya ocurrió. */}
            <Boton
              variant="outline"
              onClick={() => navigate(`/yoolpay/informe/dia?fecha=${fecha}`)}
              disabled={!fechaValida}
              title="El documento de liquidación que se le manda a la compañía"
            >
              Informe
            </Boton>
          </div>
        }
      />

      {error ? (
        <Aviso tono="alta" titulo="No se pudo cargar el día">
          {error} — la pantalla está vacía porque la llamada falló, no porque el día no
          tuviera movimiento.
        </Aviso>
      ) : null}

      {accionError ? (
        <Aviso tono="alta" titulo="La acción no se ejecutó">
          {accionError}
        </Aviso>
      ) : null}

      {accionResultado?.mensaje ? (
        <Aviso tono="baja" titulo="Listo">
          {accionResultado.mensaje}
        </Aviso>
      ) : null}

      {/* LA ADVERTENCIA NO ES UN ERROR DEL PASO, y por eso va aparte y en otro tono: son
          eventos de cierre de período que llevan semanas fallando y no tienen que ver con
          este día. Mostrarlos en rojo sobre el paso entrenaba a ignorar el rojo. */}
      {accionResultado?.advertencia ? (
        <Aviso tono="media" titulo="Pendiente, pero de otro período">
          {accionResultado.advertencia}
        </Aviso>
      ) : null}

      {!error && !data ? (
        <Seccion>
          <p style={{ padding: 24, margin: 0, fontSize: 13, color: 'var(--yool-muted)' }}>
            {cargando ? 'Cargando el día…' : 'Sin datos todavía.'}
          </p>
        </Seccion>
      ) : null}

      {!error && data ? (
        <>
          {/* 1 · LOS SEIS PASOS. Va primero y es lo único que instruye: qué falta y qué
              botón lo hace. */}
          <CierreDelDia cierre={data.cierre} acciones={accionesDelCierre} permisos={data.permisos} />

          {/* EL PASO 6, EN UN SOLO PASO. Se monta acá y no dentro de `CierreDelDia` porque
              necesita los montos por compañía, que viven en `tesoreria` — el componente de
              los pasos sólo conoce los pasos. */}
          <ModalGiros
            abierto={girosAbierto}
            pendientes={girosPendientes}
            permiso={data.permisos?.registrar_giro}
            cargando={girosCargando}
            resultados={girosResultados}
            onConfirmar={registrarGiros}
            onCerrar={() => { setGirosAbierto(false); setGirosResultados(null); }}
          />

          {/* 2 · LA VERIFICACIÓN DEL PASO 2. Va pegada a los pasos y antes del detalle
              porque responde otra pregunta: los pasos dicen QUÉ falta hacer, esto dice
              A QUIÉN le falta la plata. Un día puede tener los seis pasos en verde y una
              compañía girada de menos. */}
          <CuadraturaDelDia cuadratura={data.cuadratura} giros={data.cierre?.giros} />

          {/* 3 · EL CASO QUE SE ROMPE EN SILENCIO. Va pegado a la cuadratura porque es
              su explicación más frecuente: cuando una compañía no cuadra, muchas veces
              es un cobro que tenía que salir partido y salió por una pata. El bloque no
              se muestra si el día no tuvo cobros divididos. */}
          <PagosDivididos transacciones={data.transacciones} />

          {/* 4 · LA PLATA. De dónde salen las cifras de arriba: lo recaudado por compañía
              con su detalle por transacción —incluida la compartida entre dos
              aseguradoras— y el traspaso que hay que hacer antes de girar. */}
          <TesoreriaDelDia data={data} />

          {/* 5 · EL DETALLE FINAL. Va último de este bloque porque es donde se termina
              de resolver: los agregados de arriba dicen que hay un problema, esta lista
              dice cuál es la fila culpable. Trae todas las transacciones del día —no
              sólo las girables— así que una cuota fallida también aparece. */}
          <TransaccionesDelDia transacciones={data.transacciones} />
        </>
      ) : null}

      {/* CONCILIACIÓN PSP DEL DÍA. Endpoint distinto de `/api/dia` (spec §10.3), así que
          trae su propio token y su propia carga — no la centralizamos acá arriba porque
          no es la misma plata que arma `data`. Va FUERA del `{!error && data ? ...}` de
          arriba a propósito: si `/api/dia` falla, esta conciliación sigue siendo una
          pregunta válida y respondible, no algo que dependa de que el cierre del día haya
          cargado bien. El propio componente se guarda con `if (!fecha) return null`. */}
      <ConciliacionDelDia fecha={fechaValida} />

      {/* RESPALDO EN ODOO. Va DESPUÉS de la conciliación y por ese orden: primero
          «¿cuadra la plata?», después «¿está documentado?». Son dos preguntas
          distintas y la segunda es la que operaciones necesita responder antes de
          mandar la factura a la aseguradora.
          Fuera del bloque de `data` por lo mismo que sus vecinos: pregunta a Odoo,
          no a `/api/dia`, así que sigue siendo respondible aunque el cierre no
          haya cargado. */}
      <RespaldoOdoo fecha={fechaValida} />

      {/* BUSCADOR DE COBRO. No depende de `fecha` ni de `data` —es la herramienta de
          soporte para «me cobraron y no me aparece»— y por eso también va fuera del
          bloque de arriba: es precisamente cuando `/api/dia` está caído que a alguien de
          soporte más le va a hacer falta poder buscar un cobro puntual. Va acá porque es
          el mismo tema (conciliar contra el PSP) y aquí es donde alguien mirando este día
          lo va a buscar. */}
      <BuscadorCobro />

      {!error && data ? (
        <>
          {/* EL DETALLE DE LOS PEDIDOS. Va al final porque se lee DESPUÉS de actuar: el
              paso 5 dice «3 de 3 creados», esto dice cuáles y con qué folio.

              «Asientos del día» y «Giros del día» se fueron de acá. El primero mostraba
              tipo y estado en Odoo, que es lo que hace «Respaldo en Odoo» — con la
              diferencia de que ése LE PREGUNTA A ODOO en vez de creerle a la marca
              guardada, y esa diferencia ya costó dos días de confusión. El segundo
              repetía compañía y referencia, que ahora están en la cuadratura junto a
              quién registró cada transferencia. */}
          <PedidosDelDia pedidos={data.cierre?.pedidos} />
        </>
      ) : null}
    </div>
  );
}
